import { css } from 'lit';
/* eslint-disable max-lines */
/* eslint-disable camelcase */
import { GeoJsonGeometryTypes } from 'geojson';
import { ComboBoxOption } from 'cra-web-components/components/combo-box/combo-box';
import {
	isEventAbandonedVehicleWithValidTimestamps,
	isEventInRegion,
	isValidNumber,
} from '@/utils/utils';
import { DotNotationPath } from '@typings/shared-types';
import {
	DMSStatusType,
	DraftEvent,
	EventStatus,
	LaneImpactValue,
	LocationTypes,
	RoadEventDto,
} from '../../typings/api';
import { EventAttribute } from '../components/rem/rem-attributes';
import {
	MILLISECONDS_PER_HOUR,
	MILLISECONDS_PER_SECOND,
	REM_EVENT_COLUMNS,
	Region,
	RelativeDateTime,
	RelativeDuration,
} from '../constants';

import { EventType, EventTypes } from './ConfigREMEventTypes';
import { Districts } from './ConfigCARSx';

// list of all REM event form section names
// these form sections will be displayed in the same order as listed here
export const REM_FORM_SECTIONS = {
	DESCRIPTION: 'description',
	SOURCE: 'source',
	LOCATION: 'location',
	VISUAL_VERIFICATION: 'visual verification',
	VEHICLES: 'vehicles',
	NOTES: 'notes',
	RESTRICTIONS: 'restrictions',
	DETOURS: 'detours',
	ATTACHMENTS: 'attachments',
	SCHEDULING: 'scheduling',
	DMS: 'dms',
	RESPONDING_UNITS: 'responding units',
	ATTRIBUTES: 'attributes',
	LINKING: 'linking',
	PROJECT_CONTACTS: 'project contacts',
	NOTIFICATIONS: 'notifications',
	CAMERAS: 'cameras',
} as const;

export type FormSectionsRecord<T> = {
	[K in keyof typeof REM_FORM_SECTIONS]: T;
};

interface FormSectionConfigOptions {
	active: boolean;
	toggleable: boolean;
}

export const FormSectionConfig: FormSectionsRecord<FormSectionConfigOptions> = {
	DESCRIPTION: {
		active: true,
		toggleable: false,
	},
	SOURCE: {
		active: false,
		toggleable: false,
	},
	LOCATION: {
		active: true,
		toggleable: false,
	},
	VEHICLES: {
		active: false,
		toggleable: false,
	},
		NOTES: {
		active: true,
		toggleable: false,
	},
	RESTRICTIONS: {
		active: true,
		toggleable: false,
	},
	DETOURS: {
		active: true,
		toggleable: false,
	},
	VISUAL_VERIFICATION: {
		active: false,
		toggleable: false,
	},
	ATTACHMENTS: {
		active: false,
		toggleable: false,
	},
	SCHEDULING: {
		active: true,
		toggleable: false,
	},
	DMS: {
		active: false,
		toggleable: false,
	},
	RESPONDING_UNITS: {
		active: false,
		toggleable: false,
	},
	ATTRIBUTES: {
		active: false,
		toggleable: false,
	},
	LINKING: {
		active: false,
		toggleable: false,
	},
	NOTIFICATIONS: {
		active: true,
		toggleable: false,
	},
	PROJECT_CONTACTS: {
		active: false,
		toggleable: false,
	},
	CAMERAS: {
		active: false,
		toggleable: false,
	},
};

export enum REMExternalFilterOptions {
	ALL = 'All',
	AE = 'Active Events',
	CM = 'CONST/MAINT',
	RESTRICT = 'RESTRICTIONS',
	FUTURE = 'Future',
	AEA = 'Automated Enforcement Area',
	HW = 'High Water',
	CLOSED = 'Closed',
};

export const ConfigREMMap = {
	//  Leaflet map will center on this point, at the provided zoom level
	//  https://leafletjs.com/reference-1.4.0.html#map-setview
	defaultCenter: {
		lat: 39.76632525654491,
		lng: -86.15615844726564,
	},
	defaultZoom: 12,
	MinZoom: 7,
	MaxZoom: 18,
	pollingDelayMs: 10 * MILLISECONDS_PER_SECOND,
	fitBoundsOptions: {
		padding: 45,
		maxZoom: 13,
		minZoom: 9,
	},
	showQuickFilters: true,
	eventPopup: {
		showInternalContactDetails: true,
		showProjectContacts: false,
	},
	defaultFilter: REMExternalFilterOptions.ALL,
};

export const REMLiteTableFilters: REMExternalFilterOptions[] = [
	REMExternalFilterOptions.AE,
];

interface ExternalFilterOptionsConfig {
	excludeCompletedEvents: boolean;
	includedEventTypes: EventType[];
	excludedEventTypes: EventType[];
	additionalEventChecks?: Array<(event: RoadEventDto) => boolean>;
	onlyShowImportedEvents?: boolean;
	showFutureEvents?: boolean;
	hideCurrentEvents?: boolean;
	hideFromMap?: boolean;
	highlightPriority1Events?: boolean;
}

export const ConfigREMTable = {
	pagination: [15, 25, 50, 100],
	defaultPagination: 3,
	pollingDelayMs: 10 * MILLISECONDS_PER_SECOND,
	defaultColumns: [
		'type',
		'id',
		'route',
		'mileMarker',
		'direction',
		'county',
		'district',
		'dateCreated',
		'lastUpdated',
	] as (typeof REM_EVENT_COLUMNS)[number][],
	externalFilters: {
		[REMExternalFilterOptions.ALL]: {
			excludeCompletedEvents: true,
			includedEventTypes: [],
			excludedEventTypes: [],
			showFutureEvents: true,
		},
		[REMExternalFilterOptions.AE]: {
			excludeCompletedEvents: true,
			includedEventTypes: [
				EventTypes.STOPPED_TRAFFIC,
				EventTypes.STOP_AND_GO_TRAFFIC,
				EventTypes.HEAVY_TRAFFIC,
				EventTypes.MEDICAL_EMERGENCY,
				EventTypes.STALLED_VEHICLE,
				EventTypes.DISABLED_VEHICLE,
				EventTypes.VEHICLE_ON_FIRE,
				EventTypes.JACKKNIFED_SEMI_TRAILER,
				EventTypes.FUEL_SPILL,
				EventTypes.HAZARDOUS_MATERIALS_SPILL,
				EventTypes.SPILLED_LOAD,
				EventTypes.OVERTURNED_SEMI_TRAILER,
				EventTypes.OVERTURNED_BUS,
				EventTypes.DERAILED_TRAIN,
				EventTypes.CRASH,
				EventTypes.CRASH_INVOLVING_A_BICYCLE,
				EventTypes.CRASH_INVOLVING_A_BUS,
				EventTypes.CRASH_INVOLVING_A_MOTORCYCLE,
				EventTypes.CRASH_INVOLVING_A_PEDESTRIAN,
				EventTypes.CRASH_INVOLVING_A_TRAIN,
				EventTypes.CRASH_INVOLVING_HAZARDOUS_MATERIALS,
				EventTypes.EARLIER_CRASH,
				EventTypes.CRASH_INVOLVING_A_SEMI_TRAILER,
				EventTypes.SECONDARY_CRASH,
				EventTypes.CLOSED_TO_TRAFFIC,
				EventTypes.CLOSED,
				EventTypes.OPEN_TO_TRAFFIC,
				EventTypes.REST_AREA_CLOSED,
				EventTypes.ROAD_CLOSURE,
				EventTypes.OBSTRUCTION_ON_ROADWAY,
				EventTypes.DEBRIS_ON_ROADWAY,
				EventTypes.STORM_DAMAGE,
				EventTypes.HERD_OF_ANIMALS_ON_ROADWAY,
				EventTypes.FALLEN_TREES,
				EventTypes.DOWNED_POWER_LINES,
				EventTypes.DOWNED_CABLES,
				EventTypes.ROAD_SURFACE_COLLAPSE,
				EventTypes.PAVEMENT_BUCKLED,
				EventTypes.POTHOLE,
				EventTypes.FLOODING,
				EventTypes.BROKEN_WATER_MAIN,
				EventTypes.COLLAPSED_SEWER,
				EventTypes.GAS_LEAK,
				EventTypes.POLICE_DIRECTING_TRAFFIC,
				EventTypes.ROUGH_ROAD,
				EventTypes.VEHICLE_FIRE,
				EventTypes.FIRE,
				EventTypes.BUILDING_FIRE,
				EventTypes.LOCAL_EVENT,
				EventTypes.ROUNDABOUT,
				EventTypes.GRASS_FIRE,
				EventTypes.SLOW_TRAFFIC,
				EventTypes.LONG_QUEUES,
				EventTypes.TRAFFIC_CONGESTION,
				EventTypes.FESTIVAL,
				EventTypes.PARADE,
				EventTypes.BICYCLE_RACE,
				EventTypes.FAIR,
			],
			excludedEventTypes: [],
			showFutureEvents: true,
		},
		[REMExternalFilterOptions.CM]: {
			excludeCompletedEvents: true,
			includedEventTypes: [
				EventTypes.CLOSED_INTERMITTENTLY,
				EventTypes.OPEN_TO_TRAFFIC,
				EventTypes.LOCAL_ROAD_CLOSURES_IN_AREA,
				EventTypes.DETOUR_IN_OPERATION,
				EventTypes.INTERMITTENT_LANE_CLOSURE,
				EventTypes.ALTERNATING_LANE_CLOSURES,
				EventTypes.LONG_TERM_ROAD_CONSTRUCTION,
				EventTypes.CONSTRUCTION_WORK,
				EventTypes.PAVING_OPERATIONS,
				EventTypes.WORK_IN_THE_MEDIAN,
				EventTypes.NARROW_LANES,
				EventTypes.CONSTRUCTION_TRAFFIC_MERGING,
				EventTypes.SINGLE_LINE_TRAFFIC_ALTERNATING_DIRECTIONS,
				EventTypes.ROAD_MAINTENANCE_OPERATIONS,
				EventTypes.ROAD_MARKING_OPERATIONS,
				EventTypes.BRIDGE_MAINTENANCE_OPERATIONS,
				EventTypes.BRIDGE_CONSTRUCTION,
				EventTypes.BRIDGE_DEMOLITION_WORK,
				EventTypes.WATER_MAIN_WORK,
				EventTypes.GAS_MAIN_WORK,
				EventTypes.WORK_ON_UNDERGROUND_CABLES,
				EventTypes.WORK_ON_UNDERGROUND_SERVICES,
				EventTypes.TEMPORARY_LANE_MARKINGS,
				EventTypes.TEMPORARY_TRAFFIC_LIGHTS,
				EventTypes.EMERGENCY_MAINTENANCE,
				EventTypes.ROADSIDE_CLEANUP_CREW,
				EventTypes.BRIDGE_INSPECTION_WORK,
				EventTypes.NIGHT_TIME_CONSTRUCTION_WORK,
				EventTypes.WORK_ON_THE_SHOULDER,
				EventTypes.EMERGENCY_REPAIRS,
				EventTypes.STREET_CLEANING,
				EventTypes.UTILITY_WORK,
				EventTypes.ROADWORK,
				EventTypes.MOBILE_MAINTENANCE_OPERATIONS,
				EventTypes.CLOSED_AHEAD,
				EventTypes.CLOSED_FOR_REPAIRS,
				EventTypes.BLOCKED,
				EventTypes.REDUCED_TO_ONE_LANE,
				EventTypes.REDUCED_TO_TWO_LANES,
				EventTypes.REDUCED_TO_THREE_LANES,
				EventTypes.CONSIDER_ALTERNATE_ROUTE,
				EventTypes.CLEARED,
				EventTypes.RIGHT_LANE_CLOSED,
				EventTypes.LEFT_LANE_CLOSED,
				EventTypes.CENTER_LANE_CLOSED,
				EventTypes.RIGHT_SHOULDER_CLOSED,
				EventTypes.LEFT_SHOULDER_CLOSED,
				EventTypes.EXIT_RAMP_CLOSED,
				EventTypes.ENTRANCE_RAMP_CLOSED,
				EventTypes.THROUGH_LANES_CLOSED,
				EventTypes.SHOULDER_CLOSED,
				EventTypes.PILOT_CAR_IN_OPERATION,
				EventTypes.RIGHT_LANE_BLOCKED,
				EventTypes.TWO_RIGHT_LANES_BLOCKED,
				EventTypes.THREE_RIGHT_LANES_BLOCKED,
				EventTypes.LEFT_LANE_BLOCKED,
				EventTypes.TWO_LEFT_LANES_BLOCKED,
				EventTypes.LEFT_LANE_OF_EXIT_RAMP_CLOSED,
				EventTypes.RIGHT_LANE_OF_EXIT_RAMP_CLOSED,
				EventTypes.RAMP_BLOCKED,
				EventTypes.CLOSED_TO_ALL_TRAFFIC_EXCEPT_EMERGENCY_VEHICLES,
				EventTypes.ALL_LANES_BLOCKED,
				EventTypes.SIDEWALK_CLOSED,
				EventTypes.LEFT_TURN_BAY_CLOSED,
				EventTypes.RIGHT_TURN_BAY_CLOSED,
				EventTypes.LANE_CLOSED,
				EventTypes.ROAD_CONSTRUCTION,
				EventTypes.MAJOR_ROAD_CONSTRUCTION,
				EventTypes.ROAD_RECONSTRUCTION,
				EventTypes.OPPOSING_TRAFFIC,
				EventTypes.NEW_ROAD_CONSTRUCTION_LAYOUT,
				EventTypes.NEW_ROAD_LAYOUT,
				EventTypes.BRUSH_CONTROL,
				EventTypes.PLANNED_ROADWORK,
				EventTypes.RAMP_PARTIALLY_BLOCKED,
				EventTypes.TEMPORARY_FOUR_WAY_STOP_CONDITION,
				EventTypes.WE_APPRECIATE_YOUR_PATIENCE,
				EventTypes.EXTRA_POLICE_PATROLS_IN_OPERATION,
				EventTypes.REPAIRS_IN_PROGRESS,
				EventTypes.LOOK_OUT_FOR_FLAGGER,
				EventTypes.FINES_ARE_DOUBLED_IN_THE_WORK_ZONE,
				EventTypes.CHANGING_TRAFFIC_PATTERNS,
				EventTypes.TRUCKS_ENTERING_ROADWAY,
				EventTypes.USE_RIGHT_LANE,
				EventTypes.USE_LEFT_LANE,
				EventTypes.THROUGH_TRAFFIC_KEEP_LEFT,
				EventTypes.THROUGH_TRAFFIC_KEEP_RIGHT,
				EventTypes.NORTHBOUND_TRAFFIC,
				EventTypes.EASTBOUND_TRAFFIC,
				EventTypes.SOUTHBOUND_TRAFFIC,
				EventTypes.WESTBOUND_TRAFFIC,
				EventTypes.FOLLOW_DETOUR_SIGNS,
				EventTypes.FOLLOW_LOCAL_DETOUR,
				EventTypes.TRUCKS_ARE_RECOMMENDED_TO_AVOID_THE_AREA,
			],
			excludedEventTypes: [],
			showFutureEvents: true,
		},
		[REMExternalFilterOptions.RESTRICT]: {
			excludeCompletedEvents: true,
			includedEventTypes: [
				EventTypes.RESTRICTIONS,
				EventTypes.RAMP_RESTRICTIONS,
				EventTypes.TRUCK_RESTRICTION,
				EventTypes.SPEED_RESTRICTION,
				EventTypes.LOCAL_ACCESS_ONLY,
				EventTypes.NO_TRAILERS,
				EventTypes.NO_THROUGH_TRAFFIC,
				EventTypes.WIDTH_LIMIT,
				EventTypes.HEIGHT_LIMIT,
				EventTypes.LENGTH_LIMIT,
				EventTypes.AXLE_LOAD_LIMIT,
				EventTypes.GROSS_WEIGHT_LIMIT,
				EventTypes.NO_OVERLOADS_CHANGE_TO_OVERSIZED_LOADS,
				EventTypes.PERMITTED_OVERSIZE_LOADS_PROHIBITED,
				EventTypes.NO_COMMERCIAL_TRAILER_TRAFFIC,
			],
			excludedEventTypes: [],
		},
		[REMExternalFilterOptions.FUTURE]: {
			excludeCompletedEvents: true,
			includedEventTypes: [],
			excludedEventTypes: [],
			hideCurrentEvents: true,
			showFutureEvents: true,
		},
		[REMExternalFilterOptions.AEA]: {
			excludeCompletedEvents: true,
			includedEventTypes: [
				EventTypes.AUTOMATED_ENFORCEMENT_AREA,
			],
			excludedEventTypes: [],
		},
		[REMExternalFilterOptions.HW]: {
			excludeCompletedEvents: true,
			includedEventTypes: [
				EventTypes.FLOODING,
				EventTypes.BROKEN_WATER_MAIN,
			],
			excludedEventTypes: [],
		},
		[REMExternalFilterOptions.CLOSED]: {
			excludeCompletedEvents: false,
			includedEventTypes: [],
			excludedEventTypes: [],
			showFutureEvents: true,
			additionalEventChecks: [(event: RoadEventDto) => event.eventStatus === EventStatus.COMPLETED],
			hideFromMap: true,
		},
	} as Partial<Record<REMExternalFilterOptions, ExternalFilterOptionsConfig>>,
	defaultFilter: REMExternalFilterOptions.ALL,
};
// Configures the URLS for each endpoint to be used by the REM module
export const ConfigREMApi = {
	fields: (): string => `events/fields`,
	email: (): string => `events/email`,
	deleteEmail: (): string => `events/email/delete`,
	events: (): string => `events`,

	eventImports: (): string => `imports/events`,
	takeoverEvent: (eventId: number): string => `imports/events/${eventId}/takeover`,
	forgetEvent: (): string => `imports/events/forget`,

	workZoneRequests: `events/wzrt`,
	getWorkZoneRequests: `events/publish`,

	event: (eventId: number): string => `events/${eventId}`,
	eventTimeline: (eventId: number): string => `events/${eventId}/timeline`,
	createOrUpdate: (): string => `events`,
	routesByCountyDistrict: (): string => `locations/route`,
	countyDistrict: (route_designator: string): string =>
		`locations/route/${encodeURIComponent(route_designator)}`,
	nearbyCameras: (route_designator: string): string =>
		`locations/route/${route_designator}/cameras`,
	dmsPreviewsByREMEvent: (): string => `sign/previews`,
	dmsSignsForEvent: (): string => `sign/save/previews`,
	getSignById: (signId: number): string => `sign/id/${signId}`,
	getSignQueueById: (signId: number, signType: DMSStatusType = DMSStatusType.ACTIVE): string =>
		`sign/queue/${signType}/${signId}`,
	setDMSforRemEvent: (): string => `/api/sign/save/messages`,
	getDMSForRemEvent: (eventId: number): string => `sign/messages/${eventId}`,
	removeDMSByREMEvent: (signId: number): string => `sign/trash/${signId}`,
	currentEditors: (eventId: number): string => `/api/events/${eventId}/currentEditors`,
	allCurrentEditors: `/api/events/currentEditors`,
	prioritizeMessagesForEvent: (eventId: number): string => `/api/events/${eventId}/refresh`,
	routeExtent: (): string => `locations/geometry`,
	namedPoints: (route: string) => `locations/route/${encodeURIComponent(route)}/named-points`,
	areas: (areaType: string) => `locations/areas/${areaType}`,
	codedRoadway: (route: string) => `locations/route/${encodeURIComponent(route)}/coded-roadway`,
	eventIcon: (id: number) => `icons/events/${id}`,
	eventIconBase: () => `icons/events`,
	routesWithRamps: () => `locations/ramps/routes`,
	rampsForRoute: (route: string) => `locations/ramps/${encodeURIComponent(route)}`,
	snapToRoutePoint: () => `locations/route/snapToRoutePoint`,
	ramp: (route: string, rampId: number) => `locations/ramps/${encodeURIComponent(route)}/${rampId}`,
	duplicateEvent: (id: number) => `events/${id}/duplicate`,
	attachments: (eventId: number) => `events/${eventId}/attachments`,

	snapToRamp: `locations/route/snapToRamp`,
	requestTimeoutMs: 30 * MILLISECONDS_PER_SECOND,
	signPollingRate: 60 * MILLISECONDS_PER_SECOND,
	heartbeatFrequencyMs: 5 * MILLISECONDS_PER_SECOND,
	dateFormat: 'YYYY-MM-DDTHH:mm:ss.SSSSZ',
};

export const ConfigREMEventForm = {
	location: {
		/*
			<input pattern="" /> regex is slightly different than javascript
			equivalent pattern in javascript: /^(?:\d{1,3}(?:\.\d{1})?)$/gm
		*/
		mileMarkerPattern: '[0-9]{1,3}(?:\\.[0-9]{1})?',
		mileMarkerInvalidPattern: 'Value must be formatted "mmm.t"',
		mileMarkerValueFitted: 'Mile marker entry snapped to nearest valid value.',
		mileMarkerEndValueInvalid: 'Invalid end value removed.',
		displayDistrict: true,
		displaySubdistrict: true,
		displayUnit: true,
		displayLaneDiagram: true,
		hideLocationNotes: false,
	},
	source: {
		// FIXME: consider using intl-tel-input npm package in the future
		phoneNumberRegex: /^[+]?[(]?[0-9]{3}[)]?[-\s.]?[0-9]{3}[-\s.]?[0-9]{4,6}$/,
		phoneNumberInvalid: 'Warning: phone number may not be formatted correctly',
		ordering: [
			'CCTV',
			'Patrol Encounter',
			'State Police',
			'Phone Call',
			'Radio Call',
			'Local Law Enforcement',
			'Customer Service Call Center',
			'Email',
			'Social Media',
			'TMC Staff',
			'TMC Dashboards',
		],
		defaultSource: undefined,
	},
	affectedLanes: {
		defaultLaneBlockageType: LaneImpactValue.NA,
		showRampLaneDiagram: true,
		showLaneImpactTypes: true,
		showQuickDirectionCheckboxes: false,
		showRampImpacts: false,
	},
	vehicles: {
		defaultLicensePlateState: 'N/A',
		licensePlateNumberDelimitter: '###',
	},
	dms: {
		prioritizeMessagesToolTip:
			'Clicking the <span style="color: rgb(var(--brand-status-caution));">Refresh Queue</span> button will resort the message queues for all signs associated with this event. The message associated with this event will be displayed on the board unless there are any messages with a higher priority in the queue.',
		messagesCanBeCustomized: true,
		showRefreshQueueButton: true,
	},
	attachments: {
		fileTooltips: ['Attachments must be image, text or video files (e.g. png, jpg, txt, pdf, mp4)'],
	},

	//	scheduling formats
	wholeDatestampFormatOptions: {
		//	for displaying the current date and time in full
		hour: '2-digit',
		minute: '2-digit',
		month: 'short',
		day: '2-digit',
		year: 'numeric',
		timeZoneName: 'short',
	} as Intl.DateTimeFormatOptions,
	schedulingDateFormatOptions: {
		//	for populating the date selector inputs
	},
	schedulingTimeFormatOptions: {
		//	for populating the time selector inputs
		hour12: false,
		hour: '2-digit',
		minute: '2-digit',
	} as Intl.DateTimeFormatOptions,
	timelineDateFormatOptions: {
		//	for the daily timeline entry headlines
		month: 'short',
		day: '2-digit',
		year: 'numeric',
	} as Intl.DateTimeFormatOptions,
	timelineTimeFormatOptions: {
		//	for the time of the individual changes
		hour: '2-digit',
		minute: '2-digit',
		timeZoneName: 'short',
	} as Intl.DateTimeFormatOptions,
	dateAndTimePickerFormatOptions: {
		//	for displaying the current date and time in full for the datepicker input
		hour: '2-digit',
		minute: '2-digit',
		month: '2-digit',
		day: '2-digit',
		year: 'numeric',
		hour12: false,
	} as Intl.DateTimeFormatOptions,
	requiredFields: {
		[LocationTypes.MAJOR_ROAD]: ['eventType', 'route'],
		[LocationTypes.RAMP]: ['eventType', 'route', 'rampPk'],
		[LocationTypes.LOCAL_ROAD]: [
			'eventType',
			'route',
			'locationDetails.crossStreetStart',
		],
		[LocationTypes.AREA]: ['eventType', 'locationDetails.county'],
		[LocationTypes.GPS]: ['eventType', 'lat', 'lon'],
	} as Record<LocationTypes, Array<DotNotationPath<RoadEventDto>>>,
	showFormSectionMenu: true,
	topbarButtonTexts: {
		cancel: 'cancel',
		create: 'create',
		createAndClose: 'create & close',
		update: 'update',
		updateAndClose: 'update & close',
		complete: 'complete',
		createAndComplete: 'complete',
		reopen: 'reopen',
	},
	projectContacts: {
		maxContacts: 5,
		showContactType: false,
	},
	notifications: {
		emailEnabledDefault: false,
		addAuthorAsRecipient: false,
		districtToEmailGroup: {},
	},
	priority: {
		determinePriorityByLaneImpacts: false,
		determinePriorityByUnplannedEvents: false,
		defaultPriorityForRoadClosures: 2,
	},
	notes: {
		showProjectWebsiteUrl: false,
		projectWebsiteError:
			'This URL is invalid. Please re-enter in the following format: www.example.com',
		showInternalNotes: false,
		hideInternalContactDetails: false,
	},
	recurringScheduling: {
		showSetDatesToggle: false,
		showAllDayToggle: false,
	},
	timeline: {
		showSignUpdates: false,
		showInternalNotes: true,
	},
	importEventTo511OnReopen: true,
	attributes: {
		showAttributesWithPhrases: false,
	},
};

export const RelativeDateTimeEntries = {
	[RelativeDateTime.IMMEDIATELY]: 'Immediately',
	[RelativeDateTime.TOMORROW]: 'Tomorrow',
	[RelativeDateTime.WEEK]: 'Next Week',
	[RelativeDateTime.MONTH]: 'Next Month',
};

export const RelativeDurationEntries = {
	[RelativeDuration.INDEFINITELY]: 'Indefinitely',
	[RelativeDuration.HOUR]: 'One Hour',
	[RelativeDuration.TODAY]: 'Today',
	[RelativeDuration.DAY]: 'One Day',
	[RelativeDuration.WEEK]: 'One Week',
};

export enum EventAttributeValue {
	'Work Zone' = 'Work Zone',
	'CMV' = 'CMV',
	'Hazmat' = 'Hazmat',
	'Blocking' = 'Blocking',
	'Comment Card' = 'Comment Card',
	'Narcan' = 'Narcan',
	'Secondary Incident' = 'Secondary Incident',
}

export const EventAttributes: EventAttribute[] = [
	{
		value: EventAttributeValue['Work Zone'],
		label: EventAttributeValue['Work Zone'],
	},
	{
		value: EventAttributeValue.CMV,
		label: EventAttributeValue.CMV,
	},
	{
		value: EventAttributeValue.Hazmat,
		label: EventAttributeValue.Hazmat,
	},
	{
		value: EventAttributeValue.Blocking,
		label: EventAttributeValue.Blocking,
	},
	{
		value: EventAttributeValue['Comment Card'],
		label: EventAttributeValue['Comment Card'],
	},
	{
		value: EventAttributeValue.Narcan,
		label: EventAttributeValue.Narcan,
	},
	{
		value: EventAttributeValue['Secondary Incident'],
		label: EventAttributeValue['Secondary Incident'],
	}
];

export const REMSignPollingDelayMs = 10 * MILLISECONDS_PER_SECOND;

//	if any of these props change, the location details need to be refreshed
export const DRAFT_PROP_PATHS_THAT_INVALIDATE_LOCATION_DETAILS = [
	'route',
	'startMileMarker',
	'endMileMarker',
	'county',
	'district',
];

export const DRAFT_PROP_PATHS_THAT_INVALIDATE_SIGNS = [
	'route',
	'startMileMarker',
	'endMileMarker',
	'positiveLaneBlockage.allLanesAffected',
	'negativeLaneBlockage.allLanesAffected',
];
//	if any of these properties change, the DMS previews need to be reloaded
export const DRAFT_PROP_PATHS_THAT_INVALIDATE_DMS_PREVIEW = [
	'dateEnd',
	'dateStart',
	'eventType',
	'phrases',
	'route',
	'startMileMarker',
	'endMileMarker',
	'positiveLaneBlockage',
	'negativeLaneBlockage',
	'positiveLaneBlockageType',
	'positiveLaneBlockage.lanesAffected',
	'positiveLaneBlockage.enteranceRampAffected',
	'positiveLaneBlockage.exitRampAffected',
	'positiveLaneBlockage.insideShoulderAffected',
	'positiveLaneBlockage.outsideShoulderAffected',
	'negativeLaneBlockageType',
	'negativeLaneBlockage.lanesAffected',
	'negativeLaneBlockage.enteranceRampAffected',
	'negativeLaneBlockage.exitRampAffected',
	'negativeLaneBlockage.insideShoulderAffected',
	'negativeLaneBlockage.outsideShoulderAffected',
];

// if any of these props change, the event description preview needs to be refreshed
export const DRAFT_PROP_PATHS_THAT_INVALIDATE_DESCRIPTION = [
	'eventType',
	'phrases',
	'route',
	'positiveLaneBlockage',
	'negativeLaneBlockage',
	'positiveLaneBlockageType',
	'positiveLaneBlockage.lanesAffected',
	'positiveLaneBlockage.enteranceRampAffected',
	'positiveLaneBlockage.exitRampAffected',
	'positiveLaneBlockage.insideShoulderAffected',
	'positiveLaneBlockage.outsideShoulderAffected',
	'negativeLaneBlockageType',
	'negativeLaneBlockage.lanesAffected',
	'negativeLaneBlockage.enteranceRampAffected',
	'negativeLaneBlockage.exitRampAffected',
	'negativeLaneBlockage.insideShoulderAffected',
	'negativeLaneBlockage.outsideShoulderAffected',
	'locationDetails',
	'locationDetails.city',
	'locationDetails.county',
	'locationDetails.proximity',
	'locationDetails.crossStreetStart',
	'locationDetails.crossStreetEnd',
	'geometry',
	'additionalPublicText',
	'dateStart',
	'dateEnd',
	'quantities',
	'eventLocation',
	'positiveDirection',
	'negativeDirection',
	'rampPk',
	'detours',
	'scheduleWindows',
];

// if any of these props change, priority should be recalculated
export const DRAFT_PROP_PATHS_THAT_INVALIDATE_PRIORITY = [
	'eventType',
	'positiveLaneBlockage',
	'negativeLaneBlockage',
	'positiveLaneBlockageType',
	'positiveLaneBlockage.lanesAffected',
	'positiveLaneBlockage.enteranceRampAffected',
	'positiveLaneBlockage.exitRampAffected',
	'positiveLaneBlockage.insideShoulderAffected',
	'positiveLaneBlockage.outsideShoulderAffected',
	'negativeLaneBlockageType',
	'negativeLaneBlockage.lanesAffected',
	'negativeLaneBlockage.enteranceRampAffected',
	'negativeLaneBlockage.exitRampAffected',
	'negativeLaneBlockage.insideShoulderAffected',
	'negativeLaneBlockage.outsideShoulderAffected',
];

// if any of these props change, the event icon should be re-fetched
export const DRAFT_PROP_PATHS_THAT_INVALIDATE_ICON = [
	'eventType',
	'phrases',
	'eventLocation',
	'priorityLevel',
	'dateStart',
	'positiveLaneBlockage',
	'negativeLaneBlockage',
	'positiveLaneBlockageType',
	'positiveLaneBlockage.lanesAffected',
	'positiveLaneBlockage.enteranceRampAffected',
	'positiveLaneBlockage.exitRampAffected',
	'positiveLaneBlockage.insideShoulderAffected',
	'positiveLaneBlockage.outsideShoulderAffected',
	'negativeLaneBlockageType',
	'negativeLaneBlockage.lanesAffected',
	'negativeLaneBlockage.enteranceRampAffected',
	'negativeLaneBlockage.exitRampAffected',
	'negativeLaneBlockage.insideShoulderAffected',
	'negativeLaneBlockage.outsideShoulderAffected',
];

export const extraneousRouteOptions: ComboBoxOption[] = [
	{
		value: 'Other',
		label: 'Other',
	},
];

export const DEFAULT_PRIORITY_VALUE_FALLBACK = 5;

export const CARS_EVENT_INTEGRATION_ENABLED = true;

export const iconSizeByEventPriority = (priority: number): string => {
	if (priority === 1) return 'large';
	if (priority === 2 || priority === 3) return 'medium';

	return 'small';
};

export const iconSizes: Record<string, number> = {
	small: 20,
	medium: 28,
	large: 44,
};

// if an event occurs more than this configured time in the future, display its future icon on maps
export const FUTURE_EVENT_ICON_THRESHOLD = 2 * MILLISECONDS_PER_HOUR;

// these are the columns which will be rendered in the REM table view
export const activeREMTableColumns: (typeof REM_EVENT_COLUMNS)[number][] = [
	'type',
	'priority',
	'id',
	'route',
	'mileMarker',
	'direction',
	'county',
	'district',
	'dateCreated',
	'lastUpdated',
	'schedule',
	'lastUser',
	'display511',
	'detours',
];

export interface ClusterConfig {
	minZoom: number;
	maxZoom: number;
	maxClusterDistancePx?: number;
	minClusterMembers?: number;
	excludeEventsBy?: {
		geometryType?: [GeoJsonGeometryTypes, ...GeoJsonGeometryTypes[]];
		priority?: [number, ...number[]];
		// namespace?: [RegExp | string, ...(RegExp | string)[]];
	};
	iconSrc: (resultCount: number) => string;
	iconSizePx: {
		width: number;
		height: number;
	};
	iconZIndex: number;
}

const ZINDEX_HIGHER_FEATURE = 1000;

export const clusterConfigEvents: ClusterConfig[] = [
	{
		excludeEventsBy: {
			// geometryType: ['Polygon', 'MultiPolygon'],
			priority: [1],
			// namespace: ['WazeAlerts', 'WazeJams', 'INSEG'],
		},
		maxClusterDistancePx: 28,
		minClusterMembers: 2,
		minZoom: 0,
		maxZoom: 8,
		iconSrc: (resultCount: number): string => {
			const prefix = `/img/cluster/icon-cluster-camera-`;
			const postfix = `-solid.svg`;
			if (resultCount >= 99) {
				return `${prefix}99plus${postfix}`;
			}
			if (resultCount >= 50) {
				return `${prefix}50plus${postfix}`;
			}
			if (resultCount >= 20) {
				return `${prefix}20plus${postfix}`;
			}
			if (resultCount > 10) {
				return `${prefix}10plus${postfix}`;
			}
			return `${prefix}${resultCount}${postfix}`;
		},
		iconSizePx: {
			width: 32,
			height: 32,
		},
		iconZIndex: ZINDEX_HIGHER_FEATURE,
	},
];

export const STATE_COMBOBOX_OPTIONS: ComboBoxOption[] = [
	{ value: 'N/A', label: 'N/A' },
	{ value: 'IN', label: 'IN' },
	{ value: 'AL', label: 'AL' },
	{ value: 'AK', label: 'AK' },
	{ value: 'AZ', label: 'AZ' },
	{ value: 'AR', label: 'AR' },
	{ value: 'CA', label: 'CA' },
	{ value: 'CO', label: 'CO' },
	{ value: 'CT', label: 'CT' },
	{ value: 'DE', label: 'DE' },
	{ value: 'FL', label: 'FL' },
	{ value: 'GA', label: 'GA' },
	{ value: 'HI', label: 'HI' },
	{ value: 'ID', label: 'ID' },
	{ value: 'IL', label: 'IL' },
	{ value: 'IA', label: 'IA' },
	{ value: 'KS', label: 'KS' },
	{ value: 'KY', label: 'KY' },
	{ value: 'LA', label: 'LA' },
	{ value: 'ME', label: 'ME' },
	{ value: 'MD', label: 'MD' },
	{ value: 'MA', label: 'MA' },
	{ value: 'MI', label: 'MI' },
	{ value: 'MN', label: 'MN' },
	{ value: 'MS', label: 'MS' },
	{ value: 'MO', label: 'MO' },
	{ value: 'MT', label: 'MT' },
	{ value: 'NE', label: 'NE' },
	{ value: 'NV', label: 'NV' },
	{ value: 'NH', label: 'NH' },
	{ value: 'NJ', label: 'NJ' },
	{ value: 'NM', label: 'NM' },
	{ value: 'NY', label: 'NY' },
	{ value: 'NC', label: 'NC' },
	{ value: 'ND', label: 'ND' },
	{ value: 'OH', label: 'OH' },
	{ value: 'OK', label: 'OK' },
	{ value: 'OR', label: 'OR' },
	{ value: 'PA', label: 'PA' },
	{ value: 'RI', label: 'RI' },
	{ value: 'SC', label: 'SC' },
	{ value: 'SD', label: 'SD' },
	{ value: 'TN', label: 'TN' },
	{ value: 'TX', label: 'TX' },
	{ value: 'UT', label: 'UT' },
	{ value: 'VT', label: 'VT' },
	{ value: 'VA', label: 'VA' },
	{ value: 'WA', label: 'WA' },
	{ value: 'WV', label: 'WV' },
	{ value: 'WI', label: 'WI' },
	{ value: 'WY', label: 'WY' },
];

export const getEmptyDraftEvent = (): DraftEvent => ({
	eventType: undefined,
	eventSource: ConfigREMEventForm.source.defaultSource,
	route: undefined,
	startMileMarker: undefined,
	eventLocation: LocationTypes.MAJOR_ROAD,
	dateStart: Date.now(),
	priorityLevel: 5,
	emailEnabled: ConfigREMEventForm.notifications.emailEnabledDefault,
});

export const getEventTitle = (event: RoadEventDto): string =>
	`[#${event.id}] ${event.eventType} ${
		event.eventLocation === LocationTypes.AREA
			? ''
			: `on ${event.route}${
					isValidNumber(event.startMileMarker) ? ` @ mile ${event.startMileMarker.toFixed(1)}` : ''
				}${isValidNumber(event.endMileMarker) ? ` - ${event.endMileMarker.toFixed(1)}` : ''}`
	} 
		`.trim();

export const IMPORTED_EVENT_SOURCES = ['MSPCAD'];
